import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import * as React from 'react';
import { toast } from 'sonner';
import { SocialMediaPostViewModel } from '../../../../extViewmodels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { TextArea } from '../../../components-v2/TextArea';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { EmojiPickerButton } from '../../texting/EmojiPickerButton';
import { FileInputModal } from '../FileInputModal';
import { SocialMediaImageSearchAndDisplay } from '../SocialMediaImageSearchAndDisplay';
import { SocialMediaImagesPreviewModal } from '../SocialMediaImagesPreviewModal';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	headerLabel?: string;
	imagesLabel?: string;
	impersonationContext?: ImpersonationContextViewModel;
	messageLabel?: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	post?: SocialMediaPostViewModel;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

export const SocialMediaPostContentEditor: React.FC<IProps> = props => {
	const {
		className,
		headerLabel,
		imagesLabel,
		impersonationContext,
		messageLabel,
		onPostScheduled,
		post,
		readOnly,
		styles = [],
	} = props;
	const {
		postContent,
		postImages,
		setContent,
		setImages: _setImages,
		hasVideo,
		overlayLogo,
		hideWhiteBackground,
	} = useEditSocialMediaPostContext();
	const userSession = useUserSession();

	const inputRef = React.useRef(null);

	const onInputRef = (ref: HTMLElement) => {
		inputRef.current = ref;
	};

	const onTextInputChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setContent(e.target.value);
		},
		[setContent]
	);

	const [fileInputModalOpen, setFileInputModalOpen] = React.useState(false);
	const onRequestFileInputClose = (vm?: SocialMediaPostViewModel, cancel?: boolean) => {
		if (onPostScheduled && !cancel && vm) {
			onPostScheduled(vm);
		}
		setFileInputModalOpen(false);
	};

	const [imagePreviewModalOpen, setImagePreviewModalOpen] = React.useState(false);
	const [initialImageUrl, setInitialImageUrl] = React.useState<string>('');

	const onClickEnlargeImage = (initialUrl: string) => {
		setInitialImageUrl(initialUrl);
		setImagePreviewModalOpen(true);
	};

	const onEmojiClick = React.useCallback(
		(_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
			setContent(
				`${
					postContent.substring(0, inputRef.current.selectionStart) +
					data.emoji +
					postContent.substring(inputRef.current.selectionStart)
				}`
			);
		},
		[postContent, setContent]
	);

	const onValidateSelectedFile = (file: File) => {
		const mimeType = file.type?.toLocaleLowerCase();
		if (!(mimeType.startsWith('video/') || mimeType.startsWith('image/'))) {
			toast.error('Please select a valid image or video file.');
			return false;
		}

		if (mimeType.startsWith('video/') && !hasVideo && postImages?.length > 0) {
			toast.error('This post already has a image. Please remove the image to add a video.');
			return false;
		}

		if (mimeType.startsWith('image/') && hasVideo) {
			toast.error('This post already has a video. Please remove the video to add an image.');
			return false;
		}

		return true;
	};

	const acceptedMimeTypes =
		!hasVideo && postImages?.length > 0
			? 'image/png, image/jpeg, image/jpg, .svg'
			: 'image/png, image/jpeg, image/jpg, .svg, video/mp4, video/quicktime';

	return (
		<div className={`${css(styleSheet.container, ...styles)} social-media-post-content-editor ${className || ''}`}>
			<div className={css(styleSheet.headerContainer)}>
				{headerLabel && !readOnly && <div className={css(styleSheet.header)}>{headerLabel}</div>}
			</div>
			<div className={css(styleSheet.composerContainer)}>
				<div className={css(styleSheet.labelContainer)}>
					<div className={`emoji-label-container ${css(styleSheet.labelEmoji)}`}>
						{messageLabel && <label>{messageLabel}</label>}
					</div>
					<div className={css(styleSheet.charCount)}>
						<EmojiPickerButton onEmojiClick={!readOnly ? onEmojiClick : null} popoverPlacement={['left', 'top']} />
						{postContent?.length > 0 ? `${postContent?.length}/2200` : '2200'}
					</div>
				</div>
				<TextArea
					id='social-media-content'
					disabled={readOnly}
					styles={[styleSheet.textAreaContainer]}
					onChange={onTextInputChanged}
					ref={onInputRef}
					value={postContent || ''}
				/>
			</div>

			<div className={`add-media-container ${css(styleSheet.mediaContainer)}`}>
				<label>{imagesLabel && !readOnly ? imagesLabel : 'Media'}</label>
				<SocialMediaImageSearchAndDisplay
					onClickEnlargeImage={onClickEnlargeImage}
					hasVideo={hasVideo}
					readOnly={readOnly}
					onClickMedia={() => setFileInputModalOpen(true)}
				/>
			</div>
			<FileInputModal
				acceptedMimeTypes={acceptedMimeTypes}
				impersonationContext={impersonationContext}
				limit={hasVideo ? 1 : undefined}
				modalProps={{
					isOpen: fileInputModalOpen,
					onRequestClose: onRequestFileInputClose,
				}}
				onPostScheduled={onPostScheduled}
				onValidateSelectedFile={onValidateSelectedFile}
				post={post}
			/>

			<SocialMediaImagesPreviewModal
				modalProps={{
					isOpen: imagePreviewModalOpen,
					onRequestClose: () => setImagePreviewModalOpen(false),
				}}
				initialImageUrl={initialImageUrl || ''}
				images={postImages}
				showOverlayLogo={overlayLogo}
				logoUrl={userSession?.account?.preferences?.logo?.url || ''}
				hideWhiteBackground={hideWhiteBackground}
			/>
		</div>
	);
};
